import type RootStore from "./RootStore";
import type ServiceStore from "./ServiceStore";
import type AuthStore from "./AuthStore";
import type ViewStore from "./ViewStore";
import type ConnectionStore from "./ConnectionStore";
import type ChannelStore from "./ChannelStore";
import type NotificationStore from "./NotificationStore";
import type RequestQueueStore from "./RequestQueueStore";
import type RegistrationStore from "./RegistrationStore";
import type AppLayoutStore from "./AppLayoutStore";
import type WebApiStore from "./WebApiStore";
import type BarcodeCameraStore from "./BarcodeCameraStore";
import type FindAllStore from "./findAllStore/FindAllStore";
import type ThemeStore from "./ThemeStore";
import type { CadStore } from "./CadStore";
import type DataGridStore from "./DataGridStore/DataGridStore";
import type ScheduleGridStore from "./ScheduleGridStore/ScheduleGridStore";
import type SettingsStore from "./SettingsStore";
import type SignaturePadStore from "./SignaturePadStore";

/**
 * StoreRegistry - Type-safe store management for RootStore
 * Replaces dynamic property access with strongly typed methods
 */

export interface StoreDefinition {
  name: string;
  instance: unknown;
  type: 'service' | 'view' | 'builtin';
  isDisposable: boolean;
}

export interface ServiceStoreEntry {
  name: string;
  store: ServiceStore;
  serviceName: string;
}

export interface BuiltinStoreEntry {
  name: string;
  store: unknown;
  type: string;
}

export class StoreRegistry {
  private rootStore: RootStore;
  private serviceStores = new Map<string, ServiceStoreEntry>();
  private builtinStores = new Map<string, BuiltinStoreEntry>();
  private viewStores = new Map<string, unknown>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  /**
   * Register a service store with type safety
   */
  registerServiceStore(storeName: string, store: ServiceStore, serviceName: string): void {
    if (this.serviceStores.has(storeName)) {
      // Service store already registered
      return;
    }

    this.serviceStores.set(storeName, {
      name: storeName,
      store,
      serviceName
    });

    // Set on rootStore for backward compatibility
    (this.rootStore as any)[storeName] = store;
  }

  /**
   * Register a builtin store with type safety
   */
  registerBuiltinStore<T>(storeName: string, store: T, storeType: string): void {
    if (this.builtinStores.has(storeName)) {
      // Builtin store already registered
      return;
    }

    this.builtinStores.set(storeName, {
      name: storeName,
      store,
      type: storeType
    });

    // Set on rootStore for backward compatibility
    (this.rootStore as any)[storeName] = store;
  }

  /**
   * Register a view store with type safety
   */
  registerViewStore<T>(storeName: string, store: T): void {
    if (this.viewStores.has(storeName)) {
      // View store already registered
      return;
    }

    this.viewStores.set(storeName, store);

    // Set on rootStore for backward compatibility
    (this.rootStore as any)[storeName] = store;
  }

  /**
   * Get a service store by name with type safety
   */
  getServiceStore(storeName: string): ServiceStore | null {
    const entry = this.serviceStores.get(storeName);
    return entry ? entry.store : null;
  }

  /**
   * Get a service store by service name
   */
  getServiceStoreByServiceName(serviceName: string): ServiceStore | null {
    for (const entry of this.serviceStores.values()) {
      if (entry.serviceName === serviceName) {
        return entry.store;
      }
    }
    return null;
  }

  /**
   * Get a builtin store by name with type safety
   */
  getBuiltinStore<T = unknown>(storeName: string): T | null {
    const entry = this.builtinStores.get(storeName);
    return entry ? entry.store as T : null;
  }

  /**
   * Get a view store by name with type safety
   */
  getViewStore<T = unknown>(storeName: string): T | null {
    const store = this.viewStores.get(storeName);
    return store ? store as T : null;
  }

  /**
   * Get any store by name with type checking
   */
  getStore<T = unknown>(storeName: string): T | null {
    // Check service stores first
    const serviceStore = this.getServiceStore(storeName);
    if (serviceStore) return serviceStore as T;

    // Check builtin stores
    const builtinStore = this.getBuiltinStore<T>(storeName);
    if (builtinStore) return builtinStore;

    // Check view stores
    const viewStore = this.getViewStore<T>(storeName);
    if (viewStore) return viewStore;

    return null;
  }

  /**
   * Check if a store exists
   */
  hasStore(storeName: string): boolean {
    return this.serviceStores.has(storeName) ||
           this.builtinStores.has(storeName) ||
           this.viewStores.has(storeName);
  }

  /**
   * Unregister a store and dispose if possible
   */
  unregisterStore(storeName: string): boolean {
    // Check service stores
    if (this.serviceStores.has(storeName)) {
      const entry = this.serviceStores.get(storeName)!;
      if (entry.store && typeof entry.store.dispose === 'function') {
        entry.store.dispose();
      }
      this.serviceStores.delete(storeName);
      delete (this.rootStore as any)[storeName];
      return true;
    }

    // Check builtin stores
    if (this.builtinStores.has(storeName)) {
      const entry = this.builtinStores.get(storeName)!;
      if (entry.store && typeof (entry.store as any).dispose === 'function') {
        (entry.store as any).dispose();
      }
      this.builtinStores.delete(storeName);
      delete (this.rootStore as any)[storeName];
      return true;
    }

    // Check view stores
    if (this.viewStores.has(storeName)) {
      const store = this.viewStores.get(storeName);
      if (store && typeof (store as any).dispose === 'function') {
        (store as any).dispose();
      }
      this.viewStores.delete(storeName);
      delete (this.rootStore as any)[storeName];
      return true;
    }

    return false;
  }

  /**
   * Dispose all stores safely
   */
  disposeAll(): void {
    console.log("StoreRegistry: Disposing all registered stores...");

    // Dispose service stores
    for (const [storeName, entry] of this.serviceStores) {
      try {
        if (entry.store && typeof entry.store.dispose === 'function') {
          entry.store.dispose();
        }
      } catch (error) {
        console.error(`Error disposing service store ${storeName}:`, error);
      }
    }

    // Dispose builtin stores
    for (const [storeName, entry] of this.builtinStores) {
      try {
        if (entry.store && typeof (entry.store as any).dispose === 'function') {
          (entry.store as any).dispose();
        }
      } catch (error) {
        console.error(`Error disposing builtin store ${storeName}:`, error);
      }
    }

    // Dispose view stores
    for (const [storeName, store] of this.viewStores) {
      try {
        if (store && typeof (store as any).dispose === 'function') {
          (store as any).dispose();
        }
      } catch (error) {
        console.error(`Error disposing view store ${storeName}:`, error);
      }
    }

    // Clear all maps
    this.serviceStores.clear();
    this.builtinStores.clear();
    this.viewStores.clear();

    console.log("StoreRegistry: All stores disposed");
  }

  /**
   * Get all service stores
   */
  getAllServiceStores(): ServiceStore[] {
    return Array.from(this.serviceStores.values()).map(entry => entry.store);
  }

  /**
   * Get all service store names
   */
  getServiceStoreNames(): string[] {
    return Array.from(this.serviceStores.keys());
  }

  /**
   * Get store statistics
   */
  getStats() {
    return {
      serviceStores: this.serviceStores.size,
      builtinStores: this.builtinStores.size,
      viewStores: this.viewStores.size,
      totalStores: this.serviceStores.size + this.builtinStores.size + this.viewStores.size
    };
  }

  /**
   * Type-safe access to common stores
   */
  get authStore(): AuthStore | null {
    return this.getBuiltinStore<AuthStore>('authStore');
  }

  get viewStore(): ViewStore | null {
    return this.getBuiltinStore<ViewStore>('viewStore');
  }

  get connectionStore(): ConnectionStore | null {
    return this.getBuiltinStore<ConnectionStore>('connectionStore');
  }

  get channelStore(): ChannelStore | null {
    return this.getBuiltinStore<ChannelStore>('channelStore');
  }

  get notificationStore(): NotificationStore | null {
    return this.getBuiltinStore<NotificationStore>('notificationStore');
  }

  get requestQueueStore(): RequestQueueStore | null {
    return this.getBuiltinStore<RequestQueueStore>('requestQueueStore');
  }

  get registrationStore(): RegistrationStore | null {
    return this.getBuiltinStore<RegistrationStore>('registrationStore');
  }

  get appLayoutStore(): AppLayoutStore | null {
    return this.getBuiltinStore<AppLayoutStore>('appLayoutStore');
  }

  get webApiStore(): WebApiStore | null {
    return this.getBuiltinStore<WebApiStore>('webApiStore');
  }

  get barcodeCameraStore(): BarcodeCameraStore | null {
    return this.getBuiltinStore<BarcodeCameraStore>('barcodeCameraStore');
  }

  get findAllStore(): FindAllStore | null {
    return this.getBuiltinStore<FindAllStore>('findAllStore');
  }

  get themeStore(): ThemeStore | null {
    return this.getBuiltinStore<ThemeStore>('themeStore');
  }

  get cadStore(): CadStore | null {
    return this.getBuiltinStore<CadStore>('cadStore');
  }

  get dataGridStore(): DataGridStore | null {
    return this.getBuiltinStore<DataGridStore>('dataGridStore');
  }

  get scheduleGridStore(): ScheduleGridStore | null {
    return this.getBuiltinStore<ScheduleGridStore>('scheduleGridStore');
  }

  get settingsStore(): SettingsStore | null {
    return this.getBuiltinStore<SettingsStore>('settingsStore');
  }

  get signaturePadStore(): SignaturePadStore | null {
    return this.getBuiltinStore<SignaturePadStore>('signaturePadStore');
  }

  /**
   * Check if store has pending operations
   */
  isStoreLoading(storeName: string): boolean {
    const serviceStore = this.getServiceStore(storeName);
    if (serviceStore && typeof (serviceStore as any).isServicePending === 'boolean') {
      return (serviceStore as any).isServicePending;
    }
    return false;
  }

  /**
   * Check if store has errors
   */
  hasStoreError(storeName: string): boolean {
    const serviceStore = this.getServiceStore(storeName);
    if (serviceStore && typeof (serviceStore as any).serviceHasError === 'boolean') {
      return (serviceStore as any).serviceHasError;
    }
    return false;
  }

  /**
   * Get stores that are currently loading
   */
  getLoadingStores(): string[] {
    const loadingStores: string[] = [];
    for (const [storeName] of this.serviceStores) {
      if (this.isStoreLoading(storeName)) {
        loadingStores.push(storeName);
      }
    }
    return loadingStores;
  }

  /**
   * Get stores that have errors
   */
  getStoresWithErrors(): string[] {
    const errorStores: string[] = [];
    for (const [storeName] of this.serviceStores) {
      if (this.hasStoreError(storeName)) {
        errorStores.push(storeName);
      }
    }
    return errorStores;
  }
}